import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { buildTimezoneDisplay, getTimezoneNames } from '@vendasta/galaxy/utility/timezone';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { TimezoneOption } from './interface';

@Component({
  selector: 'glxy-timezone-selector',
  templateUrl: './timezone-selector.component.html',
  styleUrls: ['./timezone-selector.component.scss'],
})
export class TimezoneSelectorComponent implements OnInit, OnChanges {
  @HostBinding('class') class = 'glxy-timezone-selector';

  private allTimezoneOptions: TimezoneOption[] = new Array<TimezoneOption>();

  private timezone?: string;
  timezones: string[] = getTimezoneNames();

  timezoneOptions$?: Observable<TimezoneOption[]>;

  @Input() control?: UntypedFormControl;

  /** An optional override for setting the internal timezone of the selector. */
  @Input() initialTimezone?: string;

  /** This date is used to display the correct current time next to each timezone option */
  @Input() selectedDate?: Date = new Date();

  @Input() bottomSpacing?: 'default' | 'small' | 'none' | false = 'default';

  @Input() disabled = false;

  /** The outputted timezone string anytime it changes */
  @Output() timezoneChanged = new EventEmitter<string>();

  get currentTimezoneDisplay(): string {
    return this.buildTimezoneDisplay(this.timezone ?? '');
  }

  ngOnInit(): void {
    if (this.initialTimezone) {
      this.timezone = this.initialTimezone;
    }
    if (!this.control) {
      this.control = new UntypedFormControl(this.currentTimezoneDisplay);
    }
    this.generateTimezoneOptions();
    this.timezoneOptions$ = this.control.valueChanges.pipe(
      startWith(this.timezone),
      map((autoselectValue: string) => {
        if (!autoselectValue || this.control?.pristine) {
          return this.allTimezoneOptions;
        }
        const fragments = autoselectValue.split(' ').map((s: string) => s.toLowerCase());
        return this.allTimezoneOptions.filter((option) => {
          const displayWithSpaces = option.display.replace('_', ' ').toLowerCase();
          return fragments.every((fragment: string) => displayWithSpaces.indexOf(fragment) >= 0);
        });
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.initialTimezone?.currentValue) {
      const currentTimezone = changes.initialTimezone.currentValue;
      this.timezone = currentTimezone;
      if (this.control) {
        this.control.setValue(this.buildTimezoneDisplay(currentTimezone));
      }
      this.timezoneChanged.emit(this.initialTimezone);
    }
    if (changes.selectedDate || changes?.initialTimezone?.currentValue) {
      this.generateTimezoneOptions();
    }
  }

  generateTimezoneOptions(): void {
    this.allTimezoneOptions = this.timezones.map((tz) => {
      return { display: this.buildTimezoneDisplay(tz), timezone: tz };
    });
  }

  onTimeZoneChanged(timezoneChange: MatOptionSelectionChange): void {
    this.changeTimeZone(timezoneChange.source.value);
  }

  changeTimeZone(timeZone: string): void {
    this.control?.setValue(this.buildTimezoneDisplay(timeZone));
    this.timezoneChanged.emit(timeZone);
  }

  private buildTimezoneDisplay(timezone: string, userTimezone: string = this.timezone || ''): string {
    return buildTimezoneDisplay(timezone, userTimezone, this.selectedDate);
  }

  timeOptionTrack(i: number, timezoneOption: TimezoneOption): string {
    return timezoneOption.timezone;
  }

  clearInput(): void {
    this.control?.setValue('');
  }

  ensureThatTimeZoneHasAValue(): void {
    // focusout event listener on the mat-form-field fires before the onSelectionChange of mat-option
    // we want to wait 100 ms for the onSelectionChange handler to finish before this focusout handler fires
    window.setTimeout(() => {
      if (!this.control?.value || buildTimezoneDisplay(this.control.value) === '') {
        this.control?.setValue(this.currentTimezoneDisplay);
      }
    }, 100);
  }
}
